import { Component, OnInit, OnDestroy, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DataGridTemplate } from '../../../../shared/data-grid/data-grid.template';
import { FormDialogTemplate } from '../../../../shared/form-dialog/form-dialog.template';
import { HttpParams } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { PartService } from '../../phf.service';
import { PartListService } from './part-list.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { GroupDescriptor, process } from '@progress/kendo-data-query';
import { FilterableService } from '../../../mesc/filterable-service';
import { PartListFormDialogTemplate } from './part-list-dialog.template';
import { S3Service } from '../../../../shared/services/s3.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { AuthenticationService } from '../../../../auth.service';
import { ConfigurationService } from '../../../../shared/data-grid/configuration/configuration.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-parts',
  templateUrl: './part-list.component.html',
  styleUrls: ['./part-list.component.scss'],
  host: { 'class': 'router-outlet' },
})
export class PartListComponent extends DataGridTemplate implements OnInit, OnDestroy {
  formDialog: FormDialogTemplate = new PartListFormDialogTemplate(this.injector);;
  editForm: FormGroup = this.formDialog.editForm;
  searchResults: FilterableService = new FilterableService();
  groupByPlantClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  groupByProgramClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  icrClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  sortingClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  imgClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  ContainerImgClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  part: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private readonly partservice: PartService;
  private readonly s3Service: S3Service;
  private readonly configurationService: ConfigurationService;
  partListService: PartListService;
  authService: AuthenticationService;

  constructor(
    private readonly injector: Injector,
  ) {
    super();
    this.partservice = injector.get(PartService);
    this.partListService = injector.get(PartListService);
    this.s3Service = injector.get(S3Service);
    this.authService = injector.get(AuthenticationService);
    this.configurationService = injector.get(ConfigurationService);
    this.controls.width = 200;
    this.pdfSettings['landscape'] = true;
    this.pdfSettings['scale'] = 0.2;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.partListService.resetFilters();
  }

  ngOnInit() {
    this.modal = true;
    this.controls.edit = false;
    this.controls.delete = false;
    this.controls.excel = true;
    this.controls.pdf = true;
    this.controls.configure = true;
    this.configureSaveButton = true;
    this.groupable = true;
    this.resizable = true;
    this.gridState.group = []
    this.sortable = true;
    this.isServerSideFiltering = true;
    this.filterable = 'menu';
    this.controls.customButtons = this.customButtons;
    this.controls.customToolBarButtons = this.customToolBarButtons;
    this.partListService.filterChanged
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (!this.partservice.loading.value && !this.partListService.resetting) {
          this.gridState.skip = 0;
          this.refresh();
        }
      });
    this.configurationService.getUserSettings()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(response => {
        if (response != null && Object.keys(response).length) {
          this.excelColumns = [];
          this.pdfColumns = [];
          const columns = response['/PHF/Parts'].columns;
          columns.forEach(column => {
            if (column.visible && column.field !== "Attachment" && column.field !== "ContainerAttachment") {
              this.addToExportColumns(column);
            }
          });

        }
        if (this.excelColumns.length === 0) {
          this.excelColumns = this.InitialExportColumns;
          this.pdfColumns = this.InitialExportColumns;
        }
      })
  }


  /**
   * 
   * @param opts 
   */
  read(opts: any): Promise<any> {

      let options;
      const filters = this.getFilters();
      options = this.getSearchParams(null);
      return this.partservice.search(filters, '/SearchPart', true, options)
        .then(x => {
          this.mapPartData(x);
          let dataLength = 0;
          if (this.exportPDFClick.value) {
            dataLength = x.SearchResult.length;
          }
          else {
            dataLength = x.RecordCount;
          }
          return {
            data: x.SearchResult,
            total: dataLength
          } as GridDataResult;
        });
   
  }

  mapPartData(data) {
    data.SearchResult.map(part => {
      part.PercentSPM = part.PercentSPM + '%';
      if(part.Throughput.ErgoSPM === -1) part.Throughput.ErgoSPM = 'N/A';
      else part.Throughput.ErgoSPM = this.formatNumber(part.Throughput.ErgoSPM);
      if(part.Throughput.EquipmentSPM) part.Throughput.EquipmentSPM = this.formatNumber(part.Throughput.EquipmentSPM);
      if(part.Throughput.ToolingSPM) part.Throughput.ToolingSPM = this.formatNumber(part.Throughput.ToolingSPM);
      if(part.Throughput.MaterialSPM) part.Throughput.MaterialSPM = this.formatNumber(part.Throughput.MaterialSPM);
      part.OverallTarget = `${this.formatNumber(part.Throughput.OverallSPM)}/${this.formatNumber(part.TargetSPM)}`;
      part.PeopleTarget = `${this.formatNumber(part.Throughput.PeopleHandling)}/${this.formatNumber(part.HandlingTarget)}`;
      part.TruckAvailOverTemplate = `${this.formatNumber(part.Throughput.TotalTrucks)}/${this.formatNumber(part.TruckTarget)}`;
      part.OrientationCheck = this.translateOrientationCheck(part.OrientationCheck);
      if(part.Throughput.PercentSPM) part.Throughput.PercentSPM = part.Throughput.PercentSPM.toFixed(2);

      part.RoboticLoading = part.RoboticLoading ? 'Yes' : 'No';
      const filePath = 'PHF/PartPictures/' + part.AttachmentFileName;
      part.AttachmentFileName
        ? part.Attachment = this.s3Service.getS3Path(filePath)
        : part.Attachment = null;
      part.ContainerAttachmentFileName
        ? part.ContainerAttachment = this.s3Service.getS3Path('PHF/ContainerPictures/' + part.ContainerAttachmentFileName)
        : part.ContainerAttachment = null;
      return part;
    });
  }


  /**
   * 
   * @param parentEditDataItem 
   */
  create(parentEditDataItem: any): void {
    this.formDialog.isNew = true;
    this.formDialog.next(this.editDataItem);
    this.partListService.selectedPartDetail.next(this.editDataItem);
  }


  /**
   * 
   * @param dataItem 
   */
  edit(dataItem: any): void {
    this.formDialog.isNew = false;
    this.formDialog.next(dataItem);
  }


  /**
   * 
   * @param dataItem 
   * @param isNew 
   */
  save(dataItem: any, isNew: boolean): Promise<any> {
    return null;
  }


  /**
   * 
   * @param dataItem 
   */
  delete(dataItem: any): Promise<any> {
    return null;
  }


  /**
   * 
   */
  onViewPartDetail = (button: any, dataItem: any) => {
    this.partListService.selectedPartDetail.next(Object.assign({}, dataItem));
  }


  /**
   * 
   */
  partDetailClose = () => {
    this.partListService.selectedPartDetail.next(null);
  }


  /**
   * 
   */
  partDetailSave = () => {
    this.refresh();
  }



  /**
   * 
   */
  handleOptimizationClose = () => {
    this.partListService.isShowOptimization.next(false);
  }


  /**
   * 
   */
  handleOptimizationApply = (dataItem) => {
    this.handleOptimizationClose();
    this.partListService.selectedPart.next(dataItem);
  }


  /**
   * Handles Grouping by server side grouping by PlantName
   */
  handleGroupByPlant = () => {
    let groups: GroupDescriptor[] = [];
    if (!this.groupByPlantClick.value) {
      groups = [{ field: 'PlantName' }];
      this.gridState.group = groups;
      this.gridState.skip = 0;
      this.groupByPlantClick.next(true);
      this.groupByProgramClick.next(false);
    }
    else {
      this.gridState.group = groups;
      this.groupByPlantClick.next(false);
    }
    this.refresh();
  }


  /**
   * Handles Grouping by server side grouping by ProgramCode
   */
  handleGroupByProgram = () => {
    let groups: GroupDescriptor[] = [];
    if (!this.groupByProgramClick.value) {
      groups = [{ field: 'ProgramCode' }];
      this.gridState.group = groups;
      this.gridState.skip = 0;
      this.gridState.sort = [{ field: 'Name', dir: 'asc' }];
      this.groupByProgramClick.next(true);
      this.groupByPlantClick.next(false);
    }
    else {
      this.gridState.group = groups;
      this.groupByProgramClick.next(false);
    }
    this.refresh();
  }


  /**
   * 
   */
  handleLessThan90ICR = () => {
    this.gridState.skip = 0;
    if (!this.icrClick.value) {
      this.icrClick.next(true);
    }
    else {
      this.icrClick.next(false);
    }
    this.refresh();
  }

  // to export excel
  allData = (): ExcelExportData => {
    let options;
    const filters = this.getFilters();
    if (Object.keys(filters).length) { options = this.getSearchParams(1); }
    else { options = this.getSearchParams(null); }
    options = options.set('export', true.toString());

    let data: any;
    return this.partservice.search(filters, '/SearchPart', true, options)
      .then(response => {
        if (response) {
          data = response.SearchResult;
        }
        data.forEach(part => {
          part.OverallTarget = `${this.formatNumber(part.Throughput.OverallSPM)}/${this.formatNumber(part.TargetSPM)}`;
          part.PeopleTarget = `${this.formatNumber(part.Throughput.PeopleHandling)}/${this.formatNumber(part.HandlingTarget)}`;
          part.TruckAvailOverTemplate = `${this.formatNumber(part.Throughput.TotalTrucks)}/${this.formatNumber(part.TruckTarget)}`;
          part.OrientationCheck = this.translateOrientationCheck(part.OrientationCheck);
          part.RoboticLoading = part.RoboticLoading ? 'Yes' : 'No';
        });
        if (data.length > 0 && data[0] === undefined) {
          data = [];
          return {};
        }
        else {
          return {
            data: process(data, {
              group: this.gridState.group,
              sort: this.gridState.sort,
              filter: this.gridState.filter
            }).data,
            group: this.gridState.group
          };
        }
      }) as ExcelExportData
  }

  getGridClass() {
    return this.partListService.isCollapsed ?
      'flex-stretch full' : 'flex-stretch short';
  }


  /**
   * Map and return filters from gridState
   */
  private getFilters() {
    const _filters: any = {};
    if (this.gridState.filter) {
      this.gridState.filter.filters
        .forEach((f: any) => {
          // map gridState from FilterDescriptor to json object key:value format
          _filters[f.filters[0].field] = f.filters[0].value
            .toString() // Convert to string
            .split(',') // Convert comma-separated filter criteria to array
            .map(x => x.trim()); // Remove leading/trailing whitespace
        })
    }

    const _searchFilters = this.partListService.getSearchFilters();
    return { ..._filters, ..._searchFilters };
  }


  /**
   * 
   */
  private getSearchParams(pageNumber: any): HttpParams {
    let _sort: any = this.gridState.sort[0];
    const _group: any = this.gridState.group[0];
    const _allDataSearch = this.exportPDFClick.value

    if (!_sort) _sort = { field: 'Name', dir: 'asc' };
    //pageNumber is set to 1 when filtering. when filtering is undone it will show the first page of grid
    let _pageNumber;
    if (pageNumber) {
      _pageNumber = 1;
    } else {
      _pageNumber = (this.gridState.skip / this.gridState.take) + 1;
    }
    // data-grid setGrid() process grouping for kendo grid

    let httpParams = new HttpParams();
    if (_group) httpParams = httpParams.set('groupBy', _group.field);
    httpParams = httpParams.set('orderBy', _sort.field);
    httpParams = httpParams.set('isAscending', (_sort.dir === 'asc').toString());
    httpParams = httpParams.set('ICRLessthan90', (this.icrClick.value).toString());
    httpParams = httpParams.set('export', (_allDataSearch).toString());
    httpParams = httpParams.set('pageSize', this.gridState.take.toString());
    httpParams = httpParams.set('pageNumber', _pageNumber.toString());

    return httpParams;
  }


  private readonly getSpmClass = (dataItem, field): string => {
    let response: string;
    if (!dataItem || !dataItem.Throughput) return '';
    switch (dataItem.Throughput[field]) {
      case 'R':
        response = 'cgp-red';
        break;
      case 'G':
        response = 'cgp-success';
        break;
      case 'Y':
        response = 'secondary-yellow';
        break;
      default:
        response = 'cgp-gray';
        break;
    }
    return response;
  }


  /**
   * 
   * @param value 
   */
  private formatNumber(value: string, format = 2): string {
    const _val: number = parseFloat(value);
    return isNaN(_val) ? '0' : _val.toFixed(format);
  }

  handleImgClick(dataItem) {
    this.imgClick.next(true);
    this.part.next(dataItem);
  }

  isImgOpen() {
    return this.imgClick.value;
  }

  closeImg() {
    this.imgClick.next(false);
    this.part.next(null);
  }

  handleContainerImgClick(dataItem) {
    this.ContainerImgClick.next(true);
    this.part.next(dataItem);
  }

  isContainerImgOpen() {
    return this.ContainerImgClick.value;
  }

  closeContainerImg() {
    this.ContainerImgClick.next(false);
    this.part.next(null);
  }

  isDisabled = (id: string): boolean => {
    if (this.partservice.value && this.partservice.value.RecordCount > 1900 && id === 'excel') return true;
    if (this.partservice.value && this.partservice.value.RecordCount > 1900 && id === 'pdf') return true;
    if (this.authService.isPHFGuest && id == 'add') return true;
  };

  translateOrientationCheck(orientationCheck: boolean) {
    switch (orientationCheck) {
      case true:
        return 'Yes';
      case false:
        return 'No';
      default:
        return 'TBD';
    }
  }

  addToExportColumns(userSettingColumn: any) {
    const column = this.columns.find(c => c.field === userSettingColumn.field)
    this.excelColumns.push(column);
    this.pdfColumns.push(column);
  }

  /****************************************************************************
   * PART LIST TEMPLATES
   ***************************************************************************/

  //stub button for part details
  customButtons = [
    {
      id: 'detail',
      title: 'Part Details',
      iconClass: 'fa fa-share-square-o',
      callback: this.onViewPartDetail,
    },
  ]

  customToolBarButtons = [
    {
      group: true,
      buttons: [{
        id: 'GroupByPlant',
        title: 'Group By Plant',
        text: 'Group By Plant',
        callback: this.handleGroupByPlant,
        toggleable: true,
        isSelected: (): boolean => this.groupByPlantClick.value,
      },
      {
        id: 'GroupByProgram',
        title: 'Group By Program',
        text: 'Group By Program',
        callback: this.handleGroupByProgram,
        toggleable: true,
        isSelected: (): boolean => this.groupByProgramClick.value
      }]
    },
    {
      id: 'lessThan90ICR',
      title: 'Show ICR < 90%',
      text: 'Show ICR < 90%',
      callback: this.handleLessThan90ICR,
      toggleable: true,
      isSelected: (): boolean => this.icrClick.value
    },
  ]


  columns: any[] = [
    {
      field: 'Attachment',
      title: 'Part Image',
      template: {
        type: 'attachment',
        width: 32,
        height: 32,
        callback: (column, dataItem) => this.handleImgClick(dataItem),
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'Code',
      title: 'Part Code',
      width: 120,
      template: {
        type: 'action-link',
        callback: (b, x) => this.partListService.selectedPart.next(x),
      }
    },
    {
      field: 'Name',
      title: 'Part Name'
    },
    {
      field: 'Number',
      title: 'Part Number',
    },
    {
      field: 'PartType',
      title: 'Part Type',
    },
    {
      field: 'ToolSetCode',
      title: 'ToolSet Id(s)',
      isSortDisabled: false,
      sortable:true,
      filterable: true
    },
    {
      field: 'ProgramCode',
      title: 'Program',
      filterable: true,
      sortable: true,
    },
    {
      field: 'PlantName',
      title: 'Plant',
      filterable: true,
      sortable: true,
    },
    {
      field: 'PressNumber',
      title: 'PressId',
    },
    {
      field: 'PressClass',
      title: 'Press Class',
    },
    {
      field: 'ContainerAttachment',
      title: 'Container Image',
      template: {
        type: 'attachment',
        width: 32,
        height: 32,
        callback: (column, dataItem) => this.handleContainerImgClick(dataItem),
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'ContainerNumber',
      title: 'Container Id',
      filterable: false,
      sortable: false,
    },
    {
      field: 'ContainerLength',
      title: 'Container Length',
      filterable: false,
      sortable: false,
    },
    {
      field: 'ContainerWidth',
      title: 'Container Width',
      filterable: false,
      sortable: false,
    },
    {
      field: 'ContainerHeight',
      title: 'Container Height',
      filterable: false,
      sortable: false,
    },
    {
      field: 'PartsPerContainer',
      title: 'Parts Per Container',
      filterable: false,
      sortable: false,
    },
    {
      field: 'OrientationInContainer',
      title: 'Orientation in Container',
      filterable: false,
      sortable: false,
    },
    {
      field: 'OrientationCheck',
      title: 'Orientation Check',
      filterable: false,
      sortable: false,
    },
    {
      field: 'Throughput.EquipmentSPM',
      title: 'Equipment SPM',
      visible: false,
      filterable: false,
      sortable: false,
      hidden: true
    },
    {
      field: 'Throughput.ToolingSPM',
      title: 'Tooling SPM',
      filterable: false,
      sortable: false,
    },
    {
      field: 'Throughput.ErgoSPM',
      title: 'Ergo SPM',
      filterable: false,
      sortable: false,
    },
    {
      field: 'Throughput.MaterialSPM',
      title: 'Material SPM',
      filterable: false,
      sortable: false,
    },
    {
      field: 'OverallTarget',
      title: 'Overall / Target SPM',
      template: {
        type: 'customStyle',
        setClass: (obj) => this.getSpmClass(obj, 'SPMFlag')
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'Throughput.PercentSPM',
      title: 'SPM %',
      template: {
        type: 'customStyle',
        setClass: (obj) => this.getSpmClass(obj, 'SPMFlag'),
        unit: 'decimal'
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'PeopleTarget',
      title: '# of People / Target',
      template: {
        type: 'customStyle',
        setClass: (obj) => this.getSpmClass(obj, 'PeopleFlag')
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'TruckAvailOverTemplate',
      title: '# of Trucks / Target',
      template: {
        type: 'customStyle',
        setClass: (obj) => this.getSpmClass(obj, 'TruckFlag')
      },
      filterable: false,
      sortable: false,
    },
    {
      field: 'RoboticLoading',
      title: 'Auto-Capable',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'TargetSPM',
      title: 'Target SPM',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'TruckTarget',
      title: 'Truck Target',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'HandlingTarget',
      title: 'Handling Target',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'EquipmentRestriction',
      title: 'Equipment Restriction',
      visible: false,
      filterable: false,
      sortable: false,
      hidden: true
    },
    {
      field: 'SPMDeviation',
      title: 'SPM Deviation',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'PartsPerStroke',
      title: 'Parts/Stroke',
      visible: false,
      filterable: false,
      sortable: false,
    },
    {
      field: 'DieAndSystemSPMRestriction',
      title: 'Die And System SPM Restriction',
      visible: false,
      filterable: false,
      sortable: false,
    },
  ]

  InitialExportColumns = [
    {
      field: 'Code',
      title: 'Part Code',
    },
    {
      field: 'Name',
      title: 'Part Name'
    },
    {
      field: 'Number',
      title: 'Part Number'
    },
    {
      field: 'PartType',
      title: 'Part Type'
    },
    {
      field: 'ToolSetCode',
      title: 'ToolSet Id(s)'
    },
    {
      field: 'ProgramCode',
      title: 'Program'
    },
    {
      field: 'PlantName',
      title: 'Plant'
    },
    {
      field: 'ContainerNumber',
      title: 'Container Id'
    },
    {
      field: 'ContainerLength',
      title: 'Container Length'
    },
    {
      field: 'ContainerWidth',
      title: 'Container Width'
    },
    {
      field: 'ContainerHeight',
      title: 'Container Height'
    },
    {
      field: 'PartsPerContainer',
      title: 'Parts Per Container',
    },
    {
      field: 'OrientationInContainer',
      title: 'Orientation in Container',
    },
    {
      field: 'OrientationCheck',
      title: 'Orientation Check',
    },
    // {
    //   field: 'Throughput.EquipmentSPM',
    //   title: 'Equipment SPM',
    // },
    {
      field: 'Throughput.ToolingSPM',
      title: 'Tooling SPM',
    },
    {
      field: 'Throughput.ErgoSPM',
      title: 'Ergo SPM',
    },
    {
      field: 'Throughput.MaterialSPM',
      title: 'Material SPM',
    },
    {
      field: 'OverallTarget',
      title: 'Overall / Target SPM',
    },
    {
      field: 'Throughput.PercentSPM',
      title: 'SPM %',
    },
    {
      field: 'PeopleTarget',
      title: '# of People / Target',
    },
    {
      field: 'TruckAvailOverTemplate',
      title: '# of Trucks / Target',
    },
  ]
}
